import React from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import {Aux} from "../../constants";
import ReceiptCard from './ReceiptCard';

const litresOf = (r) => Number(r.litres != null ? r.litres : r.deliveryTotal) || 0;
const sumLitres = (receipts) => receipts.reduce((sum, r) => sum + litresOf(r), 0);

// A multi-select picker of unassigned collection (weight) receipts for a tank. Unlike delivery's
// SelectReceiptModal (one receipt = one tank, selecting overwrites), a collection tank's total is
// the SUM of every receipt assigned to it — a driver often vacuums one tank in several bursts,
// pausing between them, each producing its own receipt. The driver checks off however many
// receipts belong to this tank, sees a running total, and confirms once. "Manually enter volume"
// remains the fallback for when receipts haven't arrived yet.
class SelectCollectionReceiptsModal extends React.Component {
    constructor(props, context) {
        super(props, context);
        this.state = {
            viewOpen: false,
            viewReceipt: null,
            selectedIds: [],
        };
        this.openView = this.openView.bind(this);
        this.closeView = this.closeView.bind(this);
        this.toggleSelected = this.toggleSelected.bind(this);
        this.handleConfirm = this.handleConfirm.bind(this);
    }

    componentDidUpdate(prevProps) {
        // Start with a clean selection each time the modal is (re)opened.
        if (!prevProps.isOpen && this.props.isOpen) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ selectedIds: [] });
        }
    }

    openView(receipt) {
        this.setState({ viewOpen: true, viewReceipt: receipt || null });
    }

    closeView() {
        this.setState({ viewOpen: false, viewReceipt: null });
    }

    toggleSelected(receiptId) {
        this.setState((prevState) => ({
            selectedIds: prevState.selectedIds.includes(receiptId)
                ? prevState.selectedIds.filter((id) => id !== receiptId)
                : [...prevState.selectedIds, receiptId],
        }));
    }

    handleConfirm() {
        const { receipts, onConfirm } = this.props;
        const { selectedIds } = this.state;
        const available = Array.isArray(receipts) ? receipts : [];
        const selected = available.filter((r) => selectedIds.includes(r.id));
        this.setState({ selectedIds: [] });
        if (selected.length > 0) {
            onConfirm(selected);
        }
    }

    render() {
        const { isOpen, toggle, receipts, assignedReceipts, onUnassign, onManualEntry, loading, tankLabel } = this.props;
        const { viewOpen, viewReceipt, selectedIds } = this.state;
        const available = Array.isArray(receipts) ? receipts : [];
        const assigned = Array.isArray(assignedReceipts) ? assignedReceipts : [];
        const selectedTotal = sumLitres(available.filter((r) => selectedIds.includes(r.id)));
        const assignedTotal = sumLitres(assigned);

        return (
            <Modal isOpen={isOpen} toggle={toggle} size="lg">
                <ModalHeader toggle={toggle}>
                    Select collection receipts{tankLabel ? ` for ${tankLabel}` : ''}
                </ModalHeader>
                <ModalBody>
                    {loading && <p className="text-muted">Loading receipts…</p>}

                    {!loading && <Aux>
                        <h6 className="text-muted mb-1">Available</h6>
                        {available.length === 0 && <p className="text-muted">No unassigned collection receipts for this truck yet.</p>}
                        {available.map((r) => (
                            <ReceiptCard key={r.id} receipt={r} showTank={false} onView={this.openView}
                                action={
                                    <label className="d-flex align-items-center justify-content-center mb-0"
                                           style={{ cursor: 'pointer', width: '2.5em', height: '2.5em' }}>
                                        <input type="checkbox" style={{ width: '1.6em', height: '1.6em' }}
                                               checked={selectedIds.includes(r.id)}
                                               onChange={() => this.toggleSelected(r.id)} />
                                    </label>
                                } />
                        ))}
                        {selectedIds.length > 0 &&
                            <p className="text-right mb-2">
                                <strong>Selected: {selectedIds.length} receipt{selectedIds.length === 1 ? '' : 's'} · {selectedTotal.toFixed(2)}L</strong>
                            </p>
                        }

                        {/* Receipts already tied to this tank. Shown with a running total so the driver
                        can see how much has been assigned so far, and undo a mis-assignment. */}
                        { assigned.length > 0 &&
                            <h6 className="text-muted mb-1 mt-4">
                                Assigned to this tank <span className="text-muted">({assignedTotal.toFixed(2)}L so far)</span>
                            </h6>}
                        {assigned.map((r) => (
                            <ReceiptCard key={r.id} receipt={r} showTank onView={this.openView}
                                action={
                                    onUnassign
                                        ? <Button color="danger" size="lg" outline onClick={() => onUnassign(r)}>
                                            <i className="fa fa-times" />
                                          </Button>
                                        : null
                                } />
                        ))}
                    </Aux>
                    }

                </ModalBody>
                <ModalFooter>
                    <Button color="success" disabled={selectedIds.length === 0} onClick={this.handleConfirm}>
                        Confirm{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
                    </Button>
                    <Button color="primary" onClick={onManualEntry}>Manually enter volume</Button>
                    <Button color="secondary" onClick={toggle}>Cancel</Button>
                </ModalFooter>

                <Modal isOpen={viewOpen} toggle={this.closeView} size="lg">
                    <ModalHeader toggle={this.closeView}>Receipt</ModalHeader>
                    <ModalBody>
                        {viewReceipt && viewReceipt.address &&
                            <p className="mb-2"><strong>Location:</strong> {viewReceipt.address}</p>}
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 0 }}>{viewReceipt && viewReceipt.emailContent}</pre>
                        <div className="mt-2 text-right">
                            <Button color="secondary" onClick={this.closeView}>Close</Button>
                        </div>
                    </ModalBody>
                </Modal>
            </Modal>
        );
    }
}

SelectCollectionReceiptsModal.propTypes = {
    isOpen: PropTypes.bool,
    toggle: PropTypes.func.isRequired,
    receipts: PropTypes.array,
    assignedReceipts: PropTypes.array,
    onConfirm: PropTypes.func.isRequired,
    onUnassign: PropTypes.func,
    onManualEntry: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    tankLabel: PropTypes.string,
};

export default SelectCollectionReceiptsModal;
